import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { adminApi, uploadFileToSignedUrl } from '../lib/api.ts';
import { Btn } from '../components/Btn.tsx';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { Field, TextArea } from '../components/Field.tsx';
import { ErrorMessage } from '../components/ErrorMessage.tsx';
import { useAdminAuth } from '../lib/auth/admin.tsx';
import type { AuditQuestion, AssetAudit, AssetAuditAnswer } from '../lib/types.ts';

type AuditResponse = {
  audit: AssetAudit & {
    asset: { make: string | null; model: string | null; year: number | null; registration_plate: string | null } | null;
    company: { company_name: string } | null;
    asset_audit_answers: AssetAuditAnswer[];
  };
  questions: AuditQuestion[];
};

export function AdminAuditRunner() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAdminAuth();
  const queryKey = ['audit', id];

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () => adminApi.get<AuditResponse>(`/api/audits/${id}`),
    enabled: !!id,
  });

  const [notes, setNotes] = useState('');
  const [error, setError] = useState<unknown>(null);

  // Auditors run audits inside their own shell and return to "My Audits"; staff
  // run them from the asset detail page and return there.
  const isAuditor = user?.role === 'auditor';
  const backTo = isAuditor ? '/audit' : `/admin/assets/${data?.audit.asset_id}`;

  const complete = useMutation({
    mutationFn: (status: 'compliant' | 'not_compliant') =>
      adminApi.post(`/api/audits/${id}/complete`, { status, notes: notes.trim() || null }),
    onSuccess: () => navigate(backTo),
    onError: setError,
  });

  const answersByQ = useMemo(() => {
    const m = new Map<string, AssetAuditAnswer>();
    for (const a of data?.audit.asset_audit_answers ?? []) {
      if (a.question_id) m.set(a.question_id, a);
    }
    return m;
  }, [data]);

  if (isLoading) return <CenteredSpinner />;
  if (!data) return <ErrorMessage error={new Error('Audit not found')} />;
  const { audit, questions } = data;
  const assetLabel =
    [audit.asset?.year, audit.asset?.make, audit.asset?.model].filter(Boolean).join(' ') ||
    audit.asset?.registration_plate ||
    'Asset';
  // 'assigned' and 'in_progress' are both still-open states; only a finalised
  // (compliant / not_compliant) audit is read-only.
  const done = audit.status === 'compliant' || audit.status === 'not_compliant';
  const backLabel = isAuditor ? 'My audits' : assetLabel;

  const anyNonCompliant = questions.some((q) => answersByQ.get(q.id)?.is_compliant === false);

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <Link to={backTo} className="text-sm text-slate-500 hover:underline">
        ← {backLabel}
      </Link>
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Asset audit</h1>
        <p className="mt-1 text-sm text-slate-500">
          {assetLabel} · {audit.company?.company_name} · {audit.questionnaire?.name ?? 'audit'}
        </p>
      </div>

      {done ? (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
          This audit was completed and marked{' '}
          <strong>{audit.status === 'compliant' ? 'compliant' : 'not compliant'}</strong>.
        </div>
      ) : (
        <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
          Work through each item, mark it compliant or not, capture photos where needed, then
          complete the audit below.
        </div>
      )}

      <div className="space-y-3">
        {questions.map((q) => (
          <AuditItem
            key={q.id}
            auditId={id!}
            question={q}
            answer={answersByQ.get(q.id)}
            disabled={done}
            onChanged={() => qc.invalidateQueries({ queryKey })}
          />
        ))}
        {questions.length === 0 && (
          <p className="text-sm italic text-slate-400">
            This audit&apos;s questionnaire has no questions.
          </p>
        )}
      </div>

      {!done && (
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <Field label="Audit notes">
            <TextArea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Field>
          {anyNonCompliant && (
            <p className="mt-2 text-xs text-amber-700">
              One or more items are marked not compliant — completing as not compliant will raise
              corrective actions and notify the company.
            </p>
          )}
          <div className="mt-4 flex gap-2">
            <Btn onClick={() => complete.mutate('compliant')} loading={complete.isPending}>
              Mark asset compliant
            </Btn>
            <Btn variant="secondary" onClick={() => complete.mutate('not_compliant')} loading={complete.isPending}>
              Mark not compliant
            </Btn>
          </div>
          <ErrorMessage error={error} />
        </div>
      )}
    </div>
  );
}

function AuditItem({
  auditId,
  question,
  answer,
  disabled,
  onChanged,
}: {
  auditId: string;
  question: AuditQuestion;
  answer: AssetAuditAnswer | undefined;
  disabled: boolean;
  onChanged: () => void;
}) {
  const [comment, setComment] = useState(answer?.comment ?? '');
  const [error, setError] = useState<unknown>(null);

  const save = useMutation({
    mutationFn: (patch: { comment?: string | null; is_compliant?: boolean | null }) =>
      adminApi.put(`/api/audits/${auditId}/answers/${question.id}`, {
        comment: patch.comment !== undefined ? patch.comment : comment.trim() || null,
        is_compliant:
          patch.is_compliant !== undefined ? patch.is_compliant : (answer?.is_compliant ?? null),
      }),
    onSuccess: onChanged,
    onError: setError,
  });

  const uploadPhoto = useMutation({
    mutationFn: async (file: File) => {
      const presigned = await adminApi.post<{ path: string; url: string }>(
        `/api/audits/${auditId}/answers/${question.id}/photo-upload-url`,
        { file_name: file.name, mime_type: file.type || 'image/jpeg' },
      );
      await uploadFileToSignedUrl(presigned.url, file);
      // Ensure an answer row exists before attaching the photo.
      await adminApi.put(`/api/audits/${auditId}/answers/${question.id}`, {
        comment: comment.trim() || null,
        is_compliant: answer?.is_compliant ?? null,
      });
      await adminApi.put(`/api/audits/${auditId}/answers/${question.id}/photo`, {
        photo_path: presigned.path,
      });
    },
    onSuccess: onChanged,
    onError: setError,
  });

  const compliant = answer?.is_compliant;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="text-sm font-medium text-slate-900">{question.label}</div>
      {question.help_text && <div className="mt-0.5 text-xs text-slate-500">{question.help_text}</div>}

      <div className="mt-3 flex gap-2">
        <button
          disabled={disabled}
          onClick={() => save.mutate({ is_compliant: true })}
          className={`rounded-lg border px-4 py-2 text-sm font-medium transition ${
            compliant === true
              ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
              : 'border-slate-200 text-slate-600 hover:bg-slate-50'
          }`}
        >
          ✓ Compliant
        </button>
        <button
          disabled={disabled}
          onClick={() => save.mutate({ is_compliant: false })}
          className={`rounded-lg border px-4 py-2 text-sm font-medium transition ${
            compliant === false
              ? 'border-red-300 bg-red-50 text-red-700'
              : 'border-slate-200 text-slate-600 hover:bg-slate-50'
          }`}
        >
          ✗ Not compliant
        </button>
      </div>

      <div className="mt-3">
        <TextArea
          rows={2}
          placeholder="Notes (shown to the company for any corrective action)"
          value={comment}
          disabled={disabled}
          onChange={(e) => setComment(e.target.value)}
          onBlur={() => save.mutate({ comment: comment.trim() || null })}
        />
      </div>

      <div className="mt-3 flex items-center gap-3">
        {answer?.photo_url && (
          <img src={answer.photo_url} alt="" className="h-16 w-16 rounded object-cover" />
        )}
        {!disabled && (
          <label className="cursor-pointer text-xs font-medium text-blue-600 hover:underline">
            {uploadPhoto.isPending ? 'Uploading…' : answer?.photo_url ? 'Replace photo' : '+ Add photo'}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) uploadPhoto.mutate(f);
                e.target.value = '';
              }}
            />
          </label>
        )}
      </div>
      <ErrorMessage error={error} />
    </div>
  );
}
